import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef } from 'react';

import { Table, TableVariant } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { EmptyState, Loading, PageHeader } from '~/components';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardTransactionRepository } from '~/repositories/CardTransactionRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';

import { CardTransactionRow, cardTransactionColumns } from './shared';

export const PageCardTransactionsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [currentPlatform?.id, isSandbox, resetPagination]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['card-transactions', 'list', currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () =>
      CardTransactionRepository.listCardTransactions({
        limit: pageSize,
        startingAfter: cursor,
      }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
  });

  const transactions = data?.cardTransactions ?? [];
  const hasMore = Boolean(data?.hasMore);

  return (
    <>
      <PageHeader text="Card Transactions" border={false} />
      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          {isError ? (
            <EmptyState headline="Error" text="Failed to load card transactions." />
          ) : transactions.length === 0 && !hasPrevious ? (
            <EmptyState headline="No transactions" text="No card transactions found." />
          ) : (
            <Table<CardTransactionRow>
              columns={cardTransactionColumns}
              data={transactions}
              variant={TableVariant.List}
              pagination={{
                hasNext: hasMore,
                hasPrevious,
                pageIndex,
                pageSize,
                onNext: () => {
                  if (transactions.length > 0) {
                    onNext({ cursor: transactions[transactions.length - 1].id });
                  }
                },
                onPrevious,
                onPageSizeChange: setPageSize,
              }}
              onRowClick={(row) => {
                navigate(`${ROUTE.CARD_TRANSACTIONS}/edit/${row.id}`);
              }}
            />
          )}
        </Inner>
      </Loading>
    </>
  );
};
